import logger from '../../utils/logger';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { 
  Palette, Type, Image, Layout, Save, Loader2, 
  Upload, Globe, Facebook, Twitter, Instagram, Linkedin, Youtube,
  Eye, RefreshCw
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DesignEditor({ adminKey }) {
  const { t } = useLanguage();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [medias, setMedias] = useState([]);
  const [mediaDialog, setMediaDialog] = useState(false);
  const [mediaTarget, setMediaTarget] = useState(null); // 'logo' or 'favicon'

  const api = useMemo(() => ({
    headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' },
    get: (url) => fetch(`${API_URL}/api${url}`, { headers: { 'X-Admin-Key': adminKey } }).then(r => r.json()),
    put: (url, data) => fetch(`${API_URL}/api${url}`, { method: 'PUT', headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).then(r => r.json())
  }), [adminKey]);

  const fetchConfig = useCallback(async () => {
    try {
      const data = await api.get('/admin/site-config');
      setConfig(data);
    } catch (error) {
      toast.error(t('admin.config_error'));
    } finally {
      setLoading(false);
    }
  }, [api, t]);

  const fetchMedias = useCallback(async () => {
    try {
      const data = await api.get('/admin/medias');
      setMedias(data.medias || []);
    } catch (error) {
      logger.error('Error fetching medias:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchConfig();
    fetchMedias();
  }, [fetchConfig, fetchMedias]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/site-config', config);
      toast.success(t('admin.config_saved'));
    } catch (error) {
      toast.error(t('admin.config_save_error'));
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const updateNestedConfig = (key, lang, value) => {
    setConfig(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [lang]: value }
    }));
  };

  const updateSocialLink = (platform, value) => {
    setConfig(prev => ({
      ...prev,
      social_links: { ...(prev.social_links || {}), [platform]: value }
    }));
  };

  const selectMedia = (media) => {
    const url = media.url.startsWith('/') ? media.url : `/${media.url}`;
    if (mediaTarget === 'logo') {
      updateConfig('logo_url', url);
    } else if (mediaTarget === 'favicon') {
      updateConfig('favicon_url', url);
    }
    setMediaDialog(false);
    setMediaTarget(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Save Button */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">{t('admin.design_editor')}</h2>
          <p className="text-muted-foreground">{t('admin.design_subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchConfig}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('admin.refresh')}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {t('admin.save')}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="identity" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="identity"><Image className="h-4 w-4 mr-2" />{t('admin.identity')}</TabsTrigger>
          <TabsTrigger value="colors"><Palette className="h-4 w-4 mr-2" />{t('admin.colors')}</TabsTrigger>
          <TabsTrigger value="header"><Layout className="h-4 w-4 mr-2" />{t('admin.header')}</TabsTrigger>
          <TabsTrigger value="footer"><Type className="h-4 w-4 mr-2" />{t('admin.footer')}</TabsTrigger>
        </TabsList>

        {/* Identity Tab */}
        <TabsContent value="identity" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="h-5 w-5" />
                {t('admin.site_identity')}
              </CardTitle>
              <CardDescription>{t('admin.site_identity_desc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label>{t('admin.logo')}</Label>
                  <div className="flex items-center gap-4">
                    <div className="h-20 w-20 border-2 border-dashed rounded-xl flex items-center justify-center bg-muted/50">
                      {config?.logo_url ? (
                        <img 
                          src={config.logo_url.startsWith('/') ? `${API_URL}${config.logo_url}` : config.logo_url} 
                          alt="Logo" 
                          className="h-16 w-16 object-contain"
                        />
                      ) : (
                        <Image className="h-8 w-8 text-muted-foreground" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <Button variant="outline" size="sm" onClick={() => { setMediaTarget('logo'); setMediaDialog(true); }}>
                        <Upload className="h-4 w-4 mr-2" />
                        {t('admin.choose_from_media')}
                      </Button>
                      <Input 
                        placeholder={t('admin.or_enter_url')}
                        value={config?.logo_url || ''}
                        onChange={(e) => updateConfig('logo_url', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Favicon */}
                <div className="space-y-3">
                  <Label>{t('admin.favicon')}</Label>
                  <div className="flex items-center gap-4">
                    <div className="h-20 w-20 border-2 border-dashed rounded-xl flex items-center justify-center bg-muted/50">
                      {config?.favicon_url ? (
                        <img 
                          src={config.favicon_url.startsWith('/') ? `${API_URL}${config.favicon_url}` : config.favicon_url} 
                          alt="Favicon" 
                          className="h-10 w-10 object-contain"
                        />
                      ) : (
                        <Globe className="h-8 w-8 text-muted-foreground" />
                      )}
                    </div>
                    <div className="space-y-2">
                      <Button variant="outline" size="sm" onClick={() => { setMediaTarget('favicon'); setMediaDialog(true); }}>
                        <Upload className="h-4 w-4 mr-2" />
                        {t('admin.choose_from_media')}
                      </Button>
                      <Input 
                        placeholder={t('admin.or_enter_url')}
                        value={config?.favicon_url || ''}
                        onChange={(e) => updateConfig('favicon_url', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Site Name */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('admin.site_name_fr')}</Label>
                  <Input 
                    value={config?.site_name?.fr || ''} 
                    onChange={(e) => updateNestedConfig('site_name', 'fr', e.target.value)}
                    placeholder="PayHub"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.site_name_en')}</Label>
                  <Input 
                    value={config?.site_name?.en || ''} 
                    onChange={(e) => updateNestedConfig('site_name', 'en', e.target.value)}
                    placeholder="PayHub"
                  />
                </div>
              </div>

              {/* Tagline */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('admin.tagline_fr')}</Label>
                  <Input 
                    value={config?.tagline?.fr || ''} 
                    onChange={(e) => updateNestedConfig('tagline', 'fr', e.target.value)}
                    placeholder="Votre portefeuille numérique"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.tagline_en')}</Label>
                  <Input 
                    value={config?.tagline?.en || ''} 
                    onChange={(e) => updateNestedConfig('tagline', 'en', e.target.value)}
                    placeholder="Your digital wallet"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Colors Tab */}
        <TabsContent value="colors" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                {t('admin.colors_title')}
              </CardTitle>
              <CardDescription>{t('admin.colors_desc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { key: 'primary_color', label: t('admin.primary_color'), default: '#16a34a' },
                  { key: 'secondary_color', label: t('admin.secondary_color'), default: '#166534' },
                  { key: 'accent_color', label: t('admin.accent_color'), default: '#22c55e' },
                  { key: 'background_color', label: t('admin.background_color'), default: '#ffffff' }
                ].map(({ key, label, default: defaultVal }) => (
                  <div key={key} className="space-y-3">
                    <Label>{label}</Label>
                    <div className="flex items-center gap-3">
                      <div 
                        className="h-12 w-12 rounded-xl border-2 shadow-sm cursor-pointer" 
                        style={{ backgroundColor: config?.[key] || defaultVal }}
                      />
                      <div className="flex-1">
                        <Input 
                          type="color"
                          value={config?.[key] || defaultVal}
                          onChange={(e) => updateConfig(key, e.target.value)}
                          className="h-10 w-full cursor-pointer"
                        />
                        <Input 
                          value={config?.[key] || defaultVal}
                          onChange={(e) => updateConfig(key, e.target.value)}
                          className="mt-2"
                          placeholder="#000000"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Preview */}
              <div className="mt-8 p-6 rounded-xl border-2 border-dashed">
                <p className="text-sm text-muted-foreground mb-4">{t('admin.preview')}</p>
                <div className="flex gap-4">
                  <Button style={{ backgroundColor: config?.primary_color }}>{t('admin.primary_button')}</Button>
                  <Button variant="outline" style={{ borderColor: config?.secondary_color, color: config?.secondary_color }}>{t('admin.secondary_button')}</Button>
                  <span className="px-3 py-2 rounded-lg text-white" style={{ backgroundColor: config?.accent_color }}>{t('admin.accent')}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Header Tab */}
        <TabsContent value="header" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layout className="h-5 w-5" />
                {t('admin.header_config')}
              </CardTitle>
              <CardDescription>{t('admin.header_config_desc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>{t('admin.header_style')}</Label>
                <div className="grid grid-cols-3 gap-4">
                  {['default', 'centered', 'minimal'].map(style => (
                    <div 
                      key={style}
                      className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                        config?.header_style === style ? 'border-primary bg-primary/5' : 'border-muted hover:border-primary/50'
                      }`}
                      onClick={() => updateConfig('header_style', style)}
                    >
                      <p className="font-medium capitalize">{style}</p>
                      <p className="text-xs text-muted-foreground">
                        {style === 'default' && t('admin.header_default')}
                        {style === 'centered' && t('admin.header_centered')}
                        {style === 'minimal' && t('admin.header_minimal')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Footer Tab */}
        <TabsContent value="footer" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Type className="h-5 w-5" />
                {t('admin.footer_config')}
              </CardTitle>
              <CardDescription>{t('admin.footer_config_desc')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Copyright */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('admin.copyright_fr')}</Label>
                  <Input 
                    value={config?.footer_copyright?.fr || ''} 
                    onChange={(e) => updateNestedConfig('footer_copyright', 'fr', e.target.value)}
                    placeholder="© 2025 PayHub. Tous droits réservés."
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('admin.copyright_en')}</Label>
                  <Input 
                    value={config?.footer_copyright?.en || ''} 
                    onChange={(e) => updateNestedConfig('footer_copyright', 'en', e.target.value)}
                    placeholder="© 2025 PayHub. All rights reserved."
                  />
                </div>
              </div>

              {/* Social Links */}
              <div className="space-y-4">
                <Label>{t('admin.social_links')}</Label>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { key: 'facebook', icon: Facebook, label: 'Facebook' },
                    { key: 'twitter', icon: Twitter, label: 'Twitter / X' },
                    { key: 'instagram', icon: Instagram, label: 'Instagram' },
                    { key: 'linkedin', icon: Linkedin, label: 'LinkedIn' },
                    { key: 'youtube', icon: Youtube, label: 'YouTube' }
                  ].map(({ key, icon: Icon, label }) => (
                    <div key={key} className="space-y-2">
                      <Label className="flex items-center gap-2 text-sm">
                        <Icon className="h-4 w-4" />
                        {label}
                      </Label>
                      <Input 
                        value={config?.social_links?.[key] || ''} 
                        onChange={(e) => updateSocialLink(key, e.target.value)}
                        placeholder={`https://${key}.com/...`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Media Selection Dialog */}
      <Dialog open={mediaDialog} onOpenChange={setMediaDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('admin.select_media')}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-4 max-h-[400px] overflow-y-auto py-4">
            {medias.filter(m => m.content_type?.startsWith('image/')).map(media => (
              <div 
                key={media.media_id}
                className="border rounded-lg p-2 cursor-pointer hover:border-primary transition-colors"
                onClick={() => selectMedia(media)}
              >
                <img 
                  src={`${API_URL}${media.url}`} 
                  alt={media.alt_text || media.original_name}
                  className="w-full h-20 object-cover rounded"
                />
                <p className="text-xs truncate mt-1">{media.original_name}</p>
              </div>
            ))}
            {medias.filter(m => m.content_type?.startsWith('image/')).length === 0 && (
              <div className="col-span-4 text-center py-8 text-muted-foreground">
                {t('admin.no_media_available')}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
